import React, { useState, useEffect, useRef } from 'react';

function ServicesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const services = [
    {
      id: 1,
      title: 'Mental Health Experts',
      tag: 'Verified MHPs',
      image: '/assets/images/services_new/experts.jpeg',
      link: '/therapists',
      icon: 'fas fa-user-md',
      color: '#7c4bb9',
      bgColor: '#f5f0ff'
    },
    {
      id: 2,
      title: 'Sharing Wall',
      tag: 'Safe Community',
      image: '/assets/images/services_new/sharingwall.jpeg',
      link: '/community',
      icon: 'fas fa-heart',
      color: '#ec4899',
      bgColor: '#fdf2f8'
    },
    {
      id: 3,
      title: 'Self-Help Toolkit',
      tag: 'Wellness Hub',
      image: '/assets/images/services_new/self_help.jpeg',
      link: '/self-help',
      icon: 'fas fa-spa',
      color: '#10b981',
      bgColor: '#ecfdf5'
    },
    {
      id: 4,
      title: 'Events & Workshops',
      tag: 'Live Masterclasses',
      image: '/assets/images/services_new/events.jpg',
      link: '/events',
      icon: 'fas fa-calendar-alt',
      color: '#8b5cf6',
      bgColor: '#f5f3ff'
    },
    {
      id: 5,
      title: 'PsychMitra DigiStore',
      tag: 'Therapeutic Tools',
      image: '/assets/images/services_new/digistore.jpeg',
      link: '/digistore',
      icon: 'fas fa-shopping-bag',
      color: '#f59e0b',
      bgColor: '#fffbeb'
    },
    {
      id: 6,
      title: 'Articles & Blogs',
      tag: 'Expert Insights',
      image: '/assets/images/services_new/blogs.jpeg',
      link: '/blog',
      icon: 'fas fa-book-open',
      color: '#3b82f6',
      // bgColor: '#eff6ff'
    }
  ];

  return (
    <section className="services-section" id="services" ref={sectionRef}>
      {/* Ambient Floating Decorative Glows */}
      <div className="services-ambient-glow services-ambient-glow-1"></div>
      <div className="services-ambient-glow services-ambient-glow-2"></div>

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        {/* Section Header */}
        <div className={`section-title text-center services-header-anim ${isVisible ? 'header-active' : ''}`}>
          <span className="services-header-badge">
            <span className="services-badge-dot"></span>
            WHAT WE OFFER
          </span>
          <h2 className="section-title__title services-main-title">
            Our <span className="services-title-accent">Services</span>
          </h2>
          <p className="services-subtext">
            Comprehensive, evidence-based mental wellness care designed for your journey
          </p>
        </div>

        {/* 2-Column Wide Cards with Animated Effects */}
        <div className="row g-4 justify-content-center">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="col-xl-6 col-lg-6 col-md-12 d-flex"
            >
              <a
                href={service.link}
                className={`service-vis-card services-card-anim services-card-delay-${(index % 6) + 1} ${isVisible ? 'card-active' : ''
                  }`}
              >
                {/* Shimmer Light Streak Effect */}
                <div className="service-card-shimmer"></div>

                {/* Left Side: Tag, Title & Action Pill */}
                <div className="service-card-left">
                  <span
                    className="service-tag-pill"
                    style={{
                      backgroundColor: service.bgColor,
                      color: service.color
                    }}
                  >
                    <i className={service.icon}></i>
                    {service.tag}
                  </span>

                  <h3 className="service-vis-title">
                    {service.title}
                  </h3>

                  <div className="service-vis-cta">
                    <span className="service-cta-text">Explore</span>
                    <div className="service-vis-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </div>
                </div>

                {/* Right Side: Animated Image Container */}
                <div className="service-vis-img-box">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="service-card-img"
                  />
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        /* =========================================================
           SERVICES SECTION & CARD ANIMATIONS
           ========================================================= */

        .services-section {
          position: relative;
          padding: 95px 0 90px;
          background: linear-gradient(180deg, #ffffff 0%, #fbf9fe 50%, #f8f5fc 100%);
          overflow: hidden;
        }

        /* Ambient Floating Glowing Orbs */
        .services-ambient-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          z-index: 1;
          opacity: 0.6;
          animation: orbGentleFloat 12s ease-in-out infinite alternate;
        }

        .services-ambient-glow-1 {
          top: 10%;
          left: -5%;
          width: 380px;
          height: 380px;
          background: radial-gradient(circle, rgba(144, 114, 200, 0.18) 0%, transparent 70%);
        }

        .services-ambient-glow-2 {
          bottom: 5%;
          right: -5%;
          width: 420px;
          height: 420px;
          background: radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 70%);
          animation-delay: -6s;
        }

        @keyframes orbGentleFloat {
          0% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-30px) scale(1.1) translateX(20px);
          }
          100% {
            transform: translateY(15px) scale(0.95) translateX(-15px);
          }
        }

        /* Header Animations */
        .services-header-anim {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .services-header-anim.header-active {
          opacity: 1;
          transform: translateY(0);
        }

        /* Card Scroll Reveal & Stagger Animation */
        .services-card-anim {
          opacity: 0;
          transform: translateY(40px) scale(0.96);
          transition: opacity 0.75s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.75s cubic-bezier(0.34, 1.56, 0.64, 1),
                      box-shadow 0.4s ease,
                      border-color 0.4s ease !important;
          will-change: opacity, transform;
        }

        .services-card-anim.card-active {
          opacity: 1 !important;
          transform: translateY(0) scale(1) !important;
        }

        .services-card-delay-1 { transition-delay: 0.05s; }
        .services-card-delay-2 { transition-delay: 0.15s; }
        .services-card-delay-3 { transition-delay: 0.25s; }
        .services-card-delay-4 { transition-delay: 0.35s; }
        .services-card-delay-5 { transition-delay: 0.45s; }
        .services-card-delay-6 { transition-delay: 0.55s; }

        .services-header-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #f0e9ff;
          color: #7c4bb9;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 7px 22px;
          border-radius: 30px;
          margin-bottom: 12px;
          box-shadow: 0 4px 15px rgba(124, 75, 185, 0.1);
          transition: all 0.35s ease;
        }

        .services-badge-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #7c4bb9;
          box-shadow: 0 0 8px #7c4bb9;
          animation: dotPulse 2s infinite;
        }

        @keyframes dotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.6; }
        }

        .services-main-title {
          font-size: clamp(2.2rem, 4vw, 2.75rem);
          font-weight: 800;
          color: #1e1335;
          margin-top: 8px;
          letter-spacing: -0.5px;
        }

        .services-title-accent {
          background: linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          position: relative;
          display: inline-block;
        }

        .services-subtext {
          color: #6b6375;
          font-size: 1.05rem;
          max-width: 600px;
          margin: 10px auto 45px;
          line-height: 1.6;
        }

        /* Card Container & Base Styles */
        .service-vis-card {
          width: 100%;
          background-color: #ffffff;
          border-radius: 26px;
          border: 1.5px solid #ece5f8;
          padding: 26px 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 22px;
          text-decoration: none;
          box-shadow: 0 10px 30px rgba(124, 75, 185, 0.05);
          position: relative;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        /* Light Streak Sweep Animation on Hover */
        .service-card-shimmer {
          position: absolute;
          top: 0;
          left: -120%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.6),
            transparent
          );
          transform: skewX(-25deg);
          transition: left 0.75s ease;
          pointer-events: none;
          z-index: 5;
        }

        .service-vis-card:hover .service-card-shimmer {
          left: 150%;
        }

        /* Card Hover Lift & 3D Glow */
        .service-vis-card:hover {
          transform: translateY(-8px) scale(1.015);
          border-color: #c4b5fd !important;
          box-shadow: 0 22px 50px rgba(124, 75, 185, 0.15),
                      0 0 25px rgba(144, 114, 200, 0.08) !important;
        }

        .service-card-left {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          min-width: 0;
          z-index: 2;
        }

        /* Tag Pill with Icon */
        .service-tag-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          padding: 5px 14px;
          border-radius: 20px;
          margin-bottom: 12px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .service-tag-pill i {
          font-size: 11px;
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .service-vis-card:hover .service-tag-pill {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }

        .service-vis-card:hover .service-tag-pill i {
          transform: scale(1.25) rotate(12deg);
        }

        /* Title */
        .service-vis-title {
          font-size: 21px;
          font-weight: 800;
          color: #241242;
          margin: 0 0 16px;
          line-height: 1.32;
          transition: color 0.3s ease, transform 0.3s ease;
        }

        .service-vis-card:hover .service-vis-title {
          color: #7c4bb9 !important;
          transform: translateX(3px);
        }

        /* CTA Explore with Animated Arrow */
        .service-vis-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          color: #7c4bb9;
          font-weight: 700;
          font-size: 14.5px;
          transition: gap 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .service-cta-text {
          position: relative;
        }

        .service-cta-text::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 2px;
          background: #7c4bb9;
          border-radius: 2px;
          transition: width 0.3s ease;
        }

        .service-vis-card:hover .service-cta-text::after {
          width: 100%;
        }

        .service-vis-arrow {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: linear-gradient(135deg, #7c4bb9 0%, #9072c8 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          box-shadow: 0 4px 12px rgba(124, 75, 185, 0.28);
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
                      box-shadow 0.35s ease;
        }

        .service-vis-card:hover .service-vis-cta {
          gap: 14px;
        }

        .service-vis-card:hover .service-vis-arrow {
          transform: translateX(6px) scale(1.12);
          box-shadow: 0 6px 18px rgba(124, 75, 185, 0.45);
        }

        /* Right Side: Image Box with Floating Breathing & Zoom Effect */
        .service-vis-img-box {
          flex: 0 0 160px;
          width: 160px;
          height: 145px;
          border-radius: 20px;
          background: #fff;
          padding: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid #f0eaf8;
          box-shadow: 0 6px 20px rgba(124, 75, 185, 0.08);
          overflow: hidden;
          position: relative;
          z-index: 2;
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
                      border-color 0.4s ease,
                      background 0.4s ease;
        }

        .service-card-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .service-vis-card:hover .service-vis-img-box {
          transform: scale(1.04) rotate(-1deg);
          border-color: #ddd0f7;
          // background: #f4ecff;
          background: #fff;
        }

        .service-vis-card:hover .service-card-img {
          transform: scale(1.12) rotate(2deg);
        }

        /* Mobile Responsive Adjustments */
        @media (max-width: 576px) {
          .services-section {
            padding: 70px 0 60px;
          }
          .service-vis-card {
            flex-direction: column-reverse;
            text-align: center;
            padding: 24px 20px;
            gap: 16px;
          }
          .service-card-left {
            align-items: center;
          }
          .service-vis-img-box {
            width: 100%;
            max-width: 180px;
            height: 140px;
          }
        }
      `}</style>
    </section>
  );
}

export default ServicesSection;
